// src/components/ContactPage.tsx
import React, { useState } from "react";
import { Container, Row, Col, Card, Button, Form, Alert, Spinner } from "react-bootstrap";
import CommunicationService from "../services/CommunicationService";
import CustomerSupportWidget from "../components/CustomerSupportWidget";

const ContactPage: React.FC = () => {
  const [showContactForm, setShowContactForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    inquiryType: 'General Inquiry',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const companyPhone = process.env.REACT_APP_COMPANY_PHONE || '999999999';
  const companyEmail = process.env.REACT_APP_COMPANY_EMAIL || 'natakitadotcom@gmail.com';

  const inquiryTypes = [
    'General Inquiry',
    'Business Partnership',
    'Technical Support',
    'Feedback',
    'Media Inquiry',
    'Other'
  ];

  const handleQuickContact = (method: 'phone' | 'email' | 'whatsapp') => {
    if (method === 'phone') {
      window.location.href = `tel:${companyPhone}`;
    } else if (method === 'email') {
      window.location.href = `mailto:${companyEmail}`;
    } else if (method === 'whatsapp') {
      const message = encodeURIComponent('Hi! I would like to get in touch with Natakiya team.');
      window.open(`https://wa.me/${companyPhone.replace(/[^0-9]/g, '')}?text=${message}`, '_blank');
    }
  };

  const handleFormSubmit = async (channel: 'email' | 'whatsapp' | 'sms') => {
    if (!formData.name.trim() || !formData.message.trim()) {
      return;
    }

    try {
      setLoading(true);
      await CommunicationService.contactCompany(
        formData.name,
        formData.phone,
        formData.email,
        formData.inquiryType,
        formData.message,
        channel
      );
      setSuccess(true);
      setTimeout(() => {
        setSuccess(false);
        setShowContactForm(false);
        setFormData({
          name: '',
          phone: '',
          email: '',
          inquiryType: 'General Inquiry',
          message: ''
        });
      }, 3000);
    } catch (error) {
      console.error('Error sending contact form:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Container className="py-5">
        <Row className="mb-5">
          <Col>
            <div className="text-center mb-4">
              <h1 className="display-4 fw-bold" style={{ color: "#810081" }}>Contact Us</h1>
              <div 
                className="mx-auto" 
                style={{ 
                  width: "80px", 
                  height: "4px", 
                  background: "linear-gradient(45deg, #810081, #FFD600)", 
                  marginBottom: "2rem" 
                }}
              ></div>
              <p className="lead text-muted">We'd love to hear from you</p>
            </div>
          </Col>
        </Row>

        <Row className="justify-content-center mb-5">
          <Col md={8} lg={6}>
            <Card className="border-0 shadow-sm">
              <Card.Body className="p-4">
                <h2 className="mb-4 text-center" style={{ color: "#810081" }}>Get In Touch</h2>
                
                <div className="d-flex align-items-center mb-4">
                  <div className="me-3">
                    <div className="d-flex align-items-center justify-content-center rounded-circle" 
                      style={{ width: "50px", height: "50px", backgroundColor: "rgba(129, 0, 129, 0.1)" }}>
                      <i className="bi bi-telephone" style={{ fontSize: "1.5rem", color: "#810081" }}></i>
                    </div>
                  </div>
                  <div className="flex-grow-1">
                    <h5 className="mb-1">Phone</h5>
                    <p className="mb-2">
                      <a href={`tel:${companyPhone}`} className="text-decoration-none" style={{ color: "#810081" }}>{companyPhone}</a>
                    </p>
                    <div className="d-flex gap-2">
                      <Button size="sm" variant="outline-primary" onClick={() => handleQuickContact('phone')}>
                        <i className="bi bi-telephone me-1"></i>Call
                      </Button>
                      <Button size="sm" variant="outline-success" onClick={() => handleQuickContact('whatsapp')}>
                        <i className="bi bi-whatsapp me-1"></i>WhatsApp
                      </Button>
                    </div>
                  </div>
                </div>
                
                <div className="d-flex align-items-center mb-4">
                  <div className="me-3">
                    <div className="d-flex align-items-center justify-content-center rounded-circle" 
                      style={{ width: "50px", height: "50px", backgroundColor: "rgba(129, 0, 129, 0.1)" }}>
                      <i className="bi bi-envelope" style={{ fontSize: "1.5rem", color: "#810081" }}></i>
                    </div>
                  </div>
                  <div className="flex-grow-1">
                    <h5 className="mb-1">Email</h5>
                    <p className="mb-2">
                      <a href={`mailto:${companyEmail}`} className="text-decoration-none" style={{ color: "#810081" }}>{companyEmail}</a>
                    </p>
                    <Button size="sm" variant="outline-warning" onClick={() => handleQuickContact('email')}>
                      <i className="bi bi-envelope me-1"></i>Send Email
                    </Button>
                  </div>
                </div>
                
                <div className="d-flex align-items-center mb-4">
                  <div className="me-3">
                    <div className="d-flex align-items-center justify-content-center rounded-circle" 
                      style={{ width: "50px", height: "50px", backgroundColor: "rgba(129, 0, 129, 0.1)" }}>
                      <i className="bi bi-clock" style={{ fontSize: "1.5rem", color: "#810081" }}></i>
                    </div>
                  </div>
                  <div>
                    <h5 className="mb-1">Business Hours</h5>
                    <p className="mb-0">Monday - Friday: 9:00 AM - 5:00 PM</p>
                    <p className="mb-0">Saturday: 10:00 AM - 2:00 PM</p>
                    <p className="mb-0">Sunday: Closed</p>
                  </div>
                </div>

                <div className="text-center">
                  <Button 
                    variant="primary" 
                    onClick={() => setShowContactForm(!showContactForm)}
                    style={{ backgroundColor: '#810081', border: 'none' }}
                  >
                    <i className="bi bi-chat-text me-2"></i>
                    {showContactForm ? 'Hide Contact Form' : 'Send Detailed Message'}
                  </Button>
                </div>

                {/* Contact Form */}
                {showContactForm && (
                  <div className="mt-4 pt-4 border-top">
                    {success ? (
                      <Alert variant="success" className="text-center">
                        <i className="bi bi-check-circle-fill me-2"></i>
                        Message sent successfully! We'll get back to you soon.
                      </Alert>
                    ) : (
                      <Form>
                        <Row className="mb-3">
                          <Col md={6}>
                            <Form.Group>
                              <Form.Label>Name *</Form.Label>
                              <Form.Control
                                type="text"
                                value={formData.name}
                                onChange={(e) => setFormData({...formData, name: e.target.value})}
                                placeholder="Your full name"
                                required
                              />
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group>
                              <Form.Label>Phone</Form.Label>
                              <Form.Control
                                type="tel"
                                value={formData.phone}
                                onChange={(e) => setFormData({...formData, phone: e.target.value})}
                                placeholder="Your phone number"
                              />
                            </Form.Group>
                          </Col>
                        </Row>

                        <Row className="mb-3">
                          <Col md={6}>
                            <Form.Group>
                              <Form.Label>Email</Form.Label>
                              <Form.Control
                                type="email"
                                value={formData.email}
                                onChange={(e) => setFormData({...formData, email: e.target.value})}
                                placeholder="Your email address"
                              />
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group>
                              <Form.Label>Inquiry Type</Form.Label>
                              <Form.Select
                                value={formData.inquiryType}
                                onChange={(e) => setFormData({...formData, inquiryType: e.target.value})}
                              >
                                {inquiryTypes.map(type => (
                                  <option key={type} value={type}>{type}</option>
                                ))}
                              </Form.Select>
                            </Form.Group>
                          </Col>
                        </Row>

                        <Form.Group className="mb-3">
                          <Form.Label>Message *</Form.Label>
                          <Form.Control
                            as="textarea"
                            rows={4}
                            value={formData.message}
                            onChange={(e) => setFormData({...formData, message: e.target.value})}
                            placeholder="Please describe your inquiry in detail..."
                            required
                          />
                        </Form.Group>

                        <div className="d-flex gap-2 justify-content-center">
                          <Button
                            variant="warning"
                            onClick={() => handleFormSubmit('email')}
                            disabled={loading || !formData.name.trim() || !formData.message.trim()}
                          >
                            {loading ? <Spinner size="sm" className="me-1" /> : <i className="bi bi-envelope me-1"></i>}
                            Email
                          </Button>
                          <Button
                            variant="success"
                            onClick={() => handleFormSubmit('whatsapp')}
                            disabled={loading || !formData.name.trim() || !formData.message.trim()}
                          >
                            {loading ? <Spinner size="sm" className="me-1" /> : <i className="bi bi-whatsapp me-1"></i>}
                            WhatsApp
                          </Button>
                          <Button
                            variant="primary"
                            onClick={() => handleFormSubmit('sms')}
                            disabled={loading || !formData.name.trim() || !formData.message.trim()}
                          >
                            {loading ? <Spinner size="sm" className="me-1" /> : <i className="bi bi-chat-dots me-1"></i>}
                            SMS
                          </Button>
                        </div>
                      </Form>
                    )}
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
        
        <Row className="justify-content-center">
          <Col md={10} className="text-center">
            <h3 className="mb-4" style={{ color: "#810081" }}>Connect With Us On Social Media</h3>
            <div className="d-flex justify-content-center gap-4">
              <a href="#" className="text-decoration-none">
                <div className="d-flex align-items-center justify-content-center rounded-circle" 
                  style={{ width: "60px", height: "60px", backgroundColor: "rgba(129, 0, 129, 0.1)" }}>
                  <i className="bi bi-facebook" style={{ fontSize: "1.8rem", color: "#810081" }}></i>
                </div>
              </a>
              <a href="#" className="text-decoration-none">
                <div className="d-flex align-items-center justify-content-center rounded-circle" 
                  style={{ width: "60px", height: "60px", backgroundColor: "rgba(129, 0, 129, 0.1)" }}>
                  <i className="bi bi-twitter" style={{ fontSize: "1.8rem", color: "#810081" }}></i>
                </div>
              </a>
              <a href="#" className="text-decoration-none">
                <div className="d-flex align-items-center justify-content-center rounded-circle" 
                  style={{ width: "60px", height: "60px", backgroundColor: "rgba(129, 0, 129, 0.1)" }}>
                  <i className="bi bi-instagram" style={{ fontSize: "1.8rem", color: "#810081" }}></i>
                </div>
              </a>
              <a href="#" className="text-decoration-none">
                <div className="d-flex align-items-center justify-content-center rounded-circle" 
                  style={{ width: "60px", height: "60px", backgroundColor: "rgba(129, 0, 129, 0.1)" }}>
                  <i className="bi bi-youtube" style={{ fontSize: "1.8rem", color: "#810081" }}></i>
                </div>
              </a>
            </div>
          </Col>
        </Row>
      </Container>
      
      {/* Customer Support Widget */}
      {/* <CustomerSupportWidget position="bottom-right" /> */}
    </>
  );
};

export default ContactPage;
